// Dev-only portal switcher data. NOT product UI — never shown in production
// (gated by DEV_TOOLS_ENABLED). Strings here are intentionally literal (dev
// tooling is exempt from the i18n commandment).

/** On in local dev (vite) and on remote dev/staging builds where VITE_DEV_TOOLS=true.
 *  Off (and inert) in production where the flag is unset. Vite inlines the flags. */
export const DEV_TOOLS_ENABLED =
  import.meta.env.DEV || import.meta.env.VITE_DEV_TOOLS === 'true';

/** Shared demo password (seed-demo-data.ts DEMO_PASSWORD). Dev/demo accounts only. */
export const DEV_PASSWORD = 'LkqDemo2026!';

export type DevHost = 'admin' | 'autofy' | 'onetec';
/** The concept a user belongs to ('platform' = LKQ Europe, the platform owner). */
export type DevConcept = 'platform' | 'autofy' | 'onetec';

export interface DevUser {
  email: string;
  name: string;
  role: string;
  /** Which concept this user belongs to — the panel groups by this. */
  concept: DevConcept;
  /** Which host their login opens on (concept admins sign in on the admin host). */
  host: DevHost;
}

export const DEV_USERS: DevUser[] = [
  { email: 'platform.admin@lkq.test', name: 'Paula Mason', role: 'Platform admin', concept: 'platform', host: 'admin' },
  { email: 'autofy.admin@lkq.test', name: 'Andy Foster', role: 'Concept admin', concept: 'autofy', host: 'admin' },
  { email: 'owner@swiftmotors.test', name: 'Sarah Swift', role: 'Garage owner', concept: 'autofy', host: 'autofy' },
  { email: 'onetec.admin@lkq.test', name: 'Olivia Nash', role: 'Concept admin', concept: 'onetec', host: 'admin' },
  { email: 'owner@pinnacle.test', name: 'Priya Patel', role: 'Garage owner', concept: 'onetec', host: 'onetec' },
  { email: 'advisor@pinnacle.test', name: 'Sam Reed', role: 'Service advisor', concept: 'onetec', host: 'onetec' },
];

/** Concepts in panel order; 'platform' (LKQ Europe) first. */
export const DEV_CONCEPTS: DevConcept[] = ['platform', 'autofy', 'onetec'];

/** Display label + a colour legible on the panel's white surface, for a concept
 *  (the brand the user belongs to). */
export function conceptOf(concept: DevConcept): { label: string; color: string } {
  switch (concept) {
    case 'autofy': return { label: 'Autofy', color: '#D75A12' };
    case 'onetec': return { label: '1TEC', color: '#0F766E' };
    default: return { label: 'LKQ Europe', color: '#0043B8' };
  }
}

/** The host whose login screen represents a concept (LKQ Europe = the admin host). */
export function conceptLoginHost(concept: DevConcept): DevHost {
  return concept === 'platform' ? 'admin' : concept;
}

/** Brand label + a dark-panel-readable colour for each host. NOTE this is the
 *  HOST's brand (the domain you land on), not necessarily the user's concept —
 *  e.g. a concept admin signs in on the `admin` (LKQ Europe) host. */
export function brandOf(host: DevHost): { label: string; color: string } {
  switch (host) {
    case 'autofy': return { label: 'Autofy', color: '#F2864A' };
    case 'onetec': return { label: '1TEC', color: '#7FD3C0' };
    default: return { label: 'LKQ Europe', color: '#5B8DEF' };
  }
}


/** Query param that triggers cross-host auto-login on the target host. */
export const DEV_LOGIN_PARAM = '__devLogin';
/** Query param that signs out on arrival, so the login page shows in a signed-out state. */
export const DEV_LOGOUT_PARAM = '__devLogout';

/** URL that lands on a host's login page and signs out first (shows the brand's login design). */
export function loginPageUrl(host: DevHost): string {
  return `${hostOrigin(host)}/login?${DEV_LOGOUT_PARAM}=1`;
}

/** Splits the current host's first DNS label into an optional prefix + the
 *  known host slug, alongside the shared base domain — so switcher URLs match
 *  the actual deployment. Handles local (`autofy.localhost` → prefix '', base
 *  'localhost'), the slug-subdomain convention (`autofy.<base>`), AND the cloud
 *  demo's flat scheme (`lkq-autofy.colaunch.dev` → prefix 'lkq-', base
 *  'colaunch.dev'), so hostOrigin('onetec') yields 'lkq-onetec.colaunch.dev'. */
const HOST_SLUG = /^(.*?)(admin|autofy|onetec|1tec|app|www)$/;
function hostShape(): { prefix: string; base: string } {
  const [first, ...rest] = window.location.hostname.split('.');
  const m = rest.length ? first.match(HOST_SLUG) : null;
  return m ? { prefix: m[1], base: rest.join('.') } : { prefix: '', base: window.location.hostname };
}

/** Base domain shared by all portal hosts, derived from the current host. */
export function baseDomain(): string {
  return hostShape().base;
}

export function hostOrigin(host: DevHost): string {
  const port = window.location.port ? `:${window.location.port}` : '';
  const { prefix, base } = hostShape();
  return `${window.location.protocol}//${prefix}${host}.${base}${port}`;
}

/** URL that lands on the user's correct host and auto-logs-in on arrival. */
export function devLoginUrl(user: DevUser): string {
  return `${hostOrigin(user.host)}/?${DEV_LOGIN_PARAM}=${encodeURIComponent(user.email)}`;
}
